import React, { useEffect, useMemo, useState } from "react";
import "./style.scss";
import cn from "clsx";
import * as Yup from "yup";
import AuthMobileLayout from "@/components/auth/authMobileLayout";
import { withPageInitialLoad } from "@/libs/pageDataBridge";
import useIsSmallScreen from "@/hooks/useIsSmallScreen";
import AuthWebLayout from "@/components/auth/authWebLayout";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { DRAFT_STORAGE_KEY, emailValidationRegExp } from "@/constants";
import { ButtonWithLoader } from "@/components/common/buttons";
import EmailIconSrc from "@/images/emailIcon.png";
import SocialSection from "./socialSection";
import { authenticateUserByEmail } from "@/redux/actions/homepage.action";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { useHistory, useLocation } from "react-router-dom";
import { useRouteSlug } from "@/hooks";
import { selectGetAuthData } from "@/util/selectors";
import { getAuthEmail } from "@/util/auth";
import CheckoutAnalyticsEvent from "@/services/analytics/main/checkout";
import { clearFromSessionStorage } from "@/util/storage";
import { normalizePathFromQuery } from "@/utils";

enum REDIRECTION_CODE {
  Login = "201",
  SignUp = "401"
}

const Authorization = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const { t } = useTranslation("auth");
  const { isSmallScreen } = useIsSmallScreen();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const routeSlug = useRouteSlug();
  const { search } = useLocation();

  const authData = useAppSelector(selectGetAuthData);
  const initialFormValues = useMemo(
    () => ({ email: authData?.email || getAuthEmail() || "" }),
    [authData?.email]
  );

  const redirectParam = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const raw = sp.get("redirect") || "";
    return normalizePathFromQuery(raw);
  }, [location.search]);

  const isCartRedirect = useMemo(() => {
    const p = redirectParam.replace(/\/+$/, "");
    return p === "my-cart";
  }, [redirectParam]);

  const cartRedirectionUrl = useMemo(() => {
    if (!isCartRedirect) return "";

    return `/${routeSlug}/${redirectParam}`;
  }, [isCartRedirect, redirectParam, routeSlug]);

  useEffect(() => {
    clearFromSessionStorage(DRAFT_STORAGE_KEY);
  }, []);

  const formikState = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async ({ email }) => {
      try {
        const response = await dispatch(authenticateUserByEmail({ email }));
        if (response.success && response.code) {
          if (response.code === REDIRECTION_CODE.Login) {
            history.replace({
              pathname: `/${routeSlug}/login/`,
              search
            });
          } else {
            history.replace({
              pathname: `/${routeSlug}/sign-up/`,
              search
            });
          }
        }
      } catch (error: any) {
        if (error?.message) setErrorMessage(error.message);
      }
    }
  });

  const { values, errors, touched, handleChange, handleSubmit, isSubmitting } =
    formikState;

  const isInputInvalid = (key: string) => Boolean(touched[key] && errors[key]);

  const guestRedirection = () => {
    CheckoutAnalyticsEvent.continueAsGuest();
    return history.push({
      pathname: `/${routeSlug}/my-cart/`,
      state: { isGuestFlow: true }
    });
  };

  const content = (
    <div className="authContainer">
      <h3 className="authTitle">{t("heyThere")}</h3>
      <p className="authSubtitle">{t("exploreTheBestBrands")}</p>

      <form onSubmit={handleSubmit}>
        <div>
          <label
            className={cn("authInputLabel", { error: isInputInvalid("email") })}
          >
            <img src={EmailIconSrc} alt="email icon" />
            <input
              type="email"
              name="email"
              placeholder={t("enterYourEmail")}
              value={values.email}
              onChange={e => {
                if (errorMessage) setErrorMessage("");
                handleChange(e);
              }}
              aria-label="Email"
            />
          </label>

          {(errorMessage || isInputInvalid("email")) && (
            <p className="errorMessage">
              {errorMessage || t("enterValidEmail")}
            </p>
          )}
        </div>

        <ButtonWithLoader
          type="submit"
          className="authContinueButton"
          showLoader={isSubmitting}
          disabled={isSubmitting}
        >
          {t("continueTitle")}
        </ButtonWithLoader>
      </form>

      {cartRedirectionUrl && (
        <button className="continue_as_guest_btn" onClick={guestRedirection}>
          {t("continueAsGuest")}
        </button>
      )}

      <div className="authDivider">
        <span className="divideLine" />
        <span className="orText">{t("continueSocial")}</span>
        <span className="divideLine" />
      </div>

      <SocialSection />
    </div>
  );

  return isSmallScreen ? (
    <AuthMobileLayout>{content}</AuthMobileLayout>
  ) : (
    <AuthWebLayout>{content}</AuthWebLayout>
  );
};

const schema = Yup.object().shape({
  email: Yup.string().trim().matches(emailValidationRegExp).required()
});

export default withPageInitialLoad({
  pageName: "Authorization",
  page: Authorization,
  ssr: false,
  loader: true
});
